import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import {
    LayoutDashboard, Wallet, Landmark, TrendingDown, Users, ClipboardList,
    Package, Truck, ShieldAlert, Percent, Route, Sparkles, LogOut, HardHat,
    FileCheck2, Briefcase, Menu, X, Building2, Coins, GitCompare, Search, Brain, Gauge,
    BookOpen, ScrollText, FileBarChart, Shield, Lightbulb, Zap, ShoppingCart, TrendingUp, FileUp, FolderOpen, Bot, BarChart3, MessageCircle, Database, Settings as SettingsIcon, FileSpreadsheet, PackageOpen
} from "lucide-react";
import CommandPalette from "./CommandPalette";
import { can, ROLE_LABELS, ROLE_COLORS } from "../lib/rbac";

const NAV = [
    { to: "/", label: "Command Center", icon: LayoutDashboard, testId: "nav-overview" },
    { to: "/clients", label: "Client Portfolio 👤", icon: Users, testId: "nav-clients" },
    { to: "/workbook-ingest", label: "Workbook Ingest 📥", icon: FileUp, testId: "nav-workbook-ingest" },
    { to: "/data-quality", label: "Data Quality 🩺", icon: ShieldAlert, testId: "nav-data-quality" },
    { to: "/enrich", label: "Enrich Master Data ✨", icon: Sparkles, testId: "nav-enrich" },
    { to: "/tds-retention", label: "TDS · Retention · Advances 💰", icon: Percent, testId: "nav-tds-retention" },
    { to: "/pnl", label: "Live P&L 📈", icon: TrendingUp, testId: "nav-pnl" },
    { to: "/cheques", label: "Cheque Register 🧾", icon: BookOpen, testId: "nav-cheques" },
    { to: "/agents", label: "AI Agents Hub 🤖", icon: Bot, testId: "nav-agents" },
    { to: "/analytics", label: "Analytics Lab 📊", icon: BarChart3, testId: "nav-analytics" },
    { to: "/notifications", label: "Notifications 📲", icon: MessageCircle, testId: "nav-notifications" },
    { to: "/data", label: "Data Manager 🗂️", icon: Database, testId: "nav-data" },
    { to: "/team", label: "Team & Access 👥", icon: Users, testId: "nav-team" },
    { to: "/settings", label: "Settings ⚙️", icon: SettingsIcon, testId: "nav-settings" },
    { to: "/entry", label: "Quick Entry ⚡", icon: Zap, testId: "nav-entry" },
    { to: "/procurement", label: "Procurement (P2P)", icon: ShoppingCart, testId: "nav-procurement" },
    { to: "/sales", label: "Sales & CRM (Q2C)", icon: TrendingUp, testId: "nav-sales" },
    { to: "/payroll", label: "Payroll & Slips", icon: Users, testId: "nav-payroll" },
    { to: "/gst", label: "GST Dashboard", icon: Percent, testId: "nav-gst" },
    { to: "/bank-import", label: "Bank Import & Recon", icon: FileUp, testId: "nav-bank-import" },
    { to: "/vault", label: "Document Vault", icon: FolderOpen, testId: "nav-vault" },
    { to: "/intelligence", label: "Intelligence", icon: Brain, testId: "nav-intelligence" },
    { to: "/simulator", label: "What-If Simulator", icon: Gauge, testId: "nav-simulator" },
    { to: "/decisions", label: "Decision Log", icon: Lightbulb, testId: "nav-decisions" },
    { to: "/journals", label: "Journal Entries", icon: BookOpen, testId: "nav-journals" },
    { to: "/ledger", label: "General Ledger", icon: ScrollText, testId: "nav-ledger" },
    { to: "/statements", label: "Financials · P&L·BS·CF", icon: FileBarChart, testId: "nav-statements" },
    { to: "/audit", label: "Audit & Assurance", icon: Shield, testId: "nav-audit" },
    { to: "/finance", label: "Trial Balance", icon: Wallet, testId: "nav-finance" },
    { to: "/banking", label: "Banking", icon: Landmark, testId: "nav-banking" },
    { to: "/cash", label: "Cash Book", icon: Coins, testId: "nav-cash" },
    { to: "/reconciliation", label: "Bank Recon", icon: GitCompare, testId: "nav-recon" },
    { to: "/loans", label: "Loans / Debt", icon: TrendingDown, testId: "nav-loans" },
    { to: "/receivables", label: "Receivables", icon: FileCheck2, testId: "nav-receivables" },
    { to: "/payables", label: "Payables", icon: ClipboardList, testId: "nav-payables" },
    { to: "/projects", label: "Projects", icon: HardHat, testId: "nav-projects" },
    { to: "/project-billing", label: "Project Billing 📒", icon: FileSpreadsheet, testId: "nav-project-billing" },
    { to: "/inventory", label: "Inventory", icon: Package, testId: "nav-inventory" },
    { to: "/consumption", label: "Material Consumption 📦", icon: PackageOpen, testId: "nav-consumption" },
    { to: "/equipment", label: "Equipment", icon: Truck, testId: "nav-equipment" },
    { to: "/hr", label: "HR & Labour", icon: Users, testId: "nav-hr" },
    { to: "/compliance", label: "Compliance", icon: FileCheck2, testId: "nav-compliance" },
    { to: "/tenders", label: "Tenders / BD", icon: Briefcase, testId: "nav-tenders" },
    { to: "/risk", label: "Risk & Fraud", icon: ShieldAlert, testId: "nav-risk" },
    { to: "/ratios", label: "Ratios Wall", icon: Percent, testId: "nav-ratios" },
    { to: "/growth", label: "₹100 Cr Roadmap", icon: Route, testId: "nav-growth" },
    { to: "/ai-advisor", label: "AI CFO Advisor", icon: Sparkles, testId: "nav-ai" },
];

const Clock = () => {
    const [t, setT] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setT(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return (
        <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] tracking-wider uppercase">
            <span className="text-[var(--brand-amber)]">◉</span>{" "}
            {t.toLocaleString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })} IST
        </div>
    );
};

const Ticker = ({ overview }) => {
    if (!overview) return null;
    const k = overview.kpis;
    const items = [
        { l: "TURNOVER YTD", v: `₹${(k.turnover_ytd/10000000).toFixed(2)} Cr`, cls: "text-[var(--text-primary)]" },
        { l: "TARGET 18M", v: `₹${(k.turnover_target_18m/10000000).toFixed(0)} Cr`, cls: "text-amber" },
        { l: "DEBT", v: `₹${(k.total_debt/10000000).toFixed(2)} Cr`, cls: "text-neg" },
        { l: "RECEIVABLES", v: `₹${(k.receivables_total/10000000).toFixed(2)} Cr`, cls: "text-[var(--text-primary)]" },
        { l: "PAYABLES", v: `₹${(k.payables_total/10000000).toFixed(2)} Cr`, cls: "text-[var(--text-primary)]" },
        { l: "CASH", v: `₹${(k.cash_bank/10000000).toFixed(2)} Cr`, cls: "text-pos" },
        { l: "NPM", v: `${k.net_profit_margin.toFixed(1)}%`, cls: "text-pos" },
        { l: "PROJECTS", v: `${k.active_projects}`, cls: "text-[var(--text-primary)]" },
        { l: "AT RISK", v: `${k.at_risk_projects}`, cls: "text-neg" },
        { l: "ORDER BOOK", v: `₹${(k.order_book/10000000).toFixed(1)} Cr`, cls: "text-amber" },
    ];
    return (
        <div className="border-b border-panel bg-[var(--bg-panel)] overflow-hidden">
            <div className="flex items-center gap-6 px-4 py-2 whitespace-nowrap overflow-x-auto text-[11px] font-mono-fin scrollbar-hide">
                {items.map((i, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                        <span className="text-[var(--text-secondary)] font-medium">{i.l}</span>
                        <span className={`font-semibold ${i.cls}`}>{i.v}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default function Layout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [overview, setOverview] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    useEffect(() => {
        api.get("/overview").then((r) => setOverview(r.data)).catch(() => {});
    }, [loc.pathname]);

    useEffect(() => {
        if (!user) nav("/login");
    }, [user, nav]);

    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setPaletteOpen((o) => !o);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--bg-base)] text-[var(--text-primary)] grain">
            {/* Top bar */}
            <header className="sticky top-0 z-40 bg-[var(--bg-panel)] border-b border-panel shadow-sm">
                <div className="flex items-center justify-between px-4 h-14">
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="mobile-menu-toggle"
                            className="md:hidden text-[var(--text-primary)]"
                            onClick={() => setMobileOpen(!mobileOpen)}
                        >
                            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[var(--brand-amber)] flex items-center justify-center rounded">
                                <Building2 size={18} className="text-[var(--text-primary)]" strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="font-display font-semibold text-sm text-[var(--text-primary)] tracking-tight leading-none">A W CONSTRUCTIONS</div>
                                <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">CFO Command Center · FY 25-26</div>
                            </div>
                        </div>
                    </div>
                    <div className="hidden lg:block">
                        <Clock />
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="cmd-palette-btn"
                            onClick={() => setPaletteOpen(true)}
                            className="hidden md:flex items-center gap-2 border border-panel px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:border-[var(--pbi-blue)] hover:text-[var(--text-primary)] transition-colors"
                        >
                            <Search size={12} />
                            <span>Search...</span>
                            <kbd className="px-1 border border-panel text-[10px] rounded">⌘K</kbd>
                        </button>
                        <div className="text-right hidden sm:block">
                            <div className="text-xs font-semibold text-[var(--text-primary)]">{user.name}</div>
                            <div className={`text-[10px] font-medium ${ROLE_COLORS[user.role] || "text-[var(--brand-amber)]"}`}>{ROLE_LABELS[user.role] || user.role}</div>
                        </div>
                        <button
                            data-testid="logout-btn"
                            onClick={() => { logout(); nav("/login"); }}
                            className="border border-panel px-3 py-1.5 text-xs text-[var(--text-secondary)] hover:bg-[var(--pbi-blue)] hover:text-white hover:border-[var(--pbi-blue)] transition-colors flex items-center gap-1.5"
                        >
                            <LogOut size={12} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <Ticker overview={overview} />

            <div className="flex">
                {/* Sidebar */}
                <aside
                    data-testid="sidebar"
                    className={`${mobileOpen ? "fixed inset-y-0 left-0 z-30 w-64" : "hidden"} md:block md:sticky md:top-[calc(56px+34px)] md:h-[calc(100vh-56px-34px)] w-56 border-r border-panel bg-panel overflow-y-auto`}
                >
                    <nav className="py-2">
                        {NAV.filter((item) => can(user.role, item.to)).map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={item.testId}
                                    end={item.to === "/"}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 py-2 text-[13px] border-l-[3px] transition-all ${
                                            isActive
                                                ? "border-[var(--pbi-blue)] bg-[var(--pbi-blue-dim)] text-[var(--text-primary)] font-semibold"
                                                : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-panel-hover)]"
                                        }`
                                    }
                                >
                                    <Icon size={15} />
                                    <span className="truncate">{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </nav>
                    <div className="p-4 mt-2 border-t border-panel">
                        <div className="text-[9px] uppercase tracking-[0.15em] font-mono-fin text-[var(--text-muted)] mb-1">Session · Role-Scoped</div>
                        <div data-testid="role-badge" className={`text-[11px] font-mono-fin uppercase tracking-wider font-semibold ${ROLE_COLORS[user.role] || "text-[var(--text-primary)]"}`}>
                            {ROLE_LABELS[user.role] || user.role}
                        </div>
                        <div className="text-[10px] font-mono-fin text-[var(--text-secondary)] mt-1">{user.email}</div>
                        <div className="text-[9px] font-mono-fin text-[var(--text-muted)] mt-1">
                            {NAV.filter((i) => can(user.role, i.to)).length} of {NAV.length} modules
                        </div>
                    </div>
                </aside>

                <main className="flex-1 p-4 md:p-6 max-w-full overflow-x-hidden">
                    <Outlet context={{ overview }} />
                </main>
            </div>
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
    );
}
